import { ChangeDetectionStrategy, Component, inject, signal } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { AuthService } from '../../services/auth.service';

import { ButtonModule } from '@openng/optimus-ui/button';
import { CardModule } from '@openng/optimus-ui/card';
import { InputTextModule } from '@openng/optimus-ui/inputtext';
import { FloatLabelModule } from '@openng/optimus-ui/floatlabel';
import { ErrorBannerComponent } from '../shared/error-banner.component';
import { ThemeService } from '../../services/theme.service';

@Component({
  selector: 'app-login',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    ReactiveFormsModule,
    ButtonModule,
    CardModule,
    InputTextModule,
    FloatLabelModule,
    ErrorBannerComponent,
  ],
  templateUrl: './login.component.html',
  styleUrl: './login.component.scss',
})
export class LoginComponent {
  private fb = inject(FormBuilder);
  private authService = inject(AuthService);
  protected themeService = inject(ThemeService);

  // A refused Google sign-in comes back as /login?error=<code>, set by the API's callback.
  protected loginError = signal<string | null>(
    googleErrorMessage(inject(ActivatedRoute).snapshot.queryParamMap.get('error')),
  );
  protected showPassword = signal(false);

  protected loginForm: FormGroup = this.fb.group({
    email: ['', [Validators.required, Validators.email]],
    password: ['', [Validators.required]],
  });

  protected signInWithGoogle() {
    this.authService.loginWithGoogle();
  }

  protected onSubmit() {
    if (!this.loginForm.valid) return;

    this.loginError.set(null);
    this.authService.login(this.loginForm.value).subscribe({
      error: (err) =>
        this.loginError.set(
          err.status === 429
            ? 'Too many attempts. Try again in a minute.'
            : [401, 403].includes(err.status)
              ? 'Wrong email or password.'
              : 'Login failed.',
        ),
    });
  }

  protected toggleTheme() {
    this.themeService.toggleTheme();
  }
}

function googleErrorMessage(code: string | null): string | null {
  switch (code) {
    case null:
      return null;
    case 'domain':
      return 'Use your @knowzilla.eu Google account.';
    case 'not_authorized':
      return 'This account has no backoffice access.';
    case 'state':
      return 'Sign-in expired. Please try again.';
    default:
      return 'Google sign-in failed.';
  }
}
